import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

// ============================================================================
// Support → Levelezés: a support postafiók (pl. support@bgfox.hu) beszélgetései.
// Beszélgetés azonosító: L-<id>, task azonosító: T-<id>.
// ============================================================================

export type SupportMailThreadStatus = 'open' | 'closed' | 'spam';
/** A lista szűrői: nyitott, válaszra vár (a vevő írt utoljára), olvasatlan, partnerek, lezárt, spam, mind (spam nélkül). */
export type SupportMailFilter = 'open' | 'waiting' | 'unread' | 'partner' | 'closed' | 'spam' | 'all';

/** Partner a címjegyzékben (UPS, Spring GDS…); az `email` teljes cím vagy „@domain.hu". */
export interface SupportMailPartner {
  id: number;
  name: string;
  email: string;
  note: string;
}

export interface SupportMailAddress {
  email: string;
  name?: string | null;
}

export interface SupportMailOrderRef {
  orderId: number;
  displayId: string;
  customerName: string;
  shop: string;
}

export interface SupportMailTicketRef {
  id: number;
  /** pl. 'T-123' */
  displayId: string;
  title: string;
  status: string;
  priority: string;
}

export interface SupportMailThreadSummary {
  id: number;
  /** pl. 'L-45' */
  displayId: string;
  subject: string;
  customerEmail: string;
  customerName: string;
  status: SupportMailThreadStatus;
  unread: boolean;
  lastMessageAt: string;
  lastDirection: 'in' | 'out';
  preview: string;
  messageCount: number;
  orderCount: number;
  ticketCount: number;
  /** a címjegyzékbeli partner neve, ha a másik fél partner */
  partnerName: string | null;
}

export interface SupportMailAttachment {
  id: number;
  fileName: string;
  mimeType: string;
  size: number;
  inline: boolean;
}

export interface SupportMailMessage {
  id: number;
  direction: 'in' | 'out';
  fromEmail: string;
  fromName: string;
  to: SupportMailAddress[];
  cc: SupportMailAddress[];
  subject: string;
  /** beérkezett levél HTML-je (szerveren tisztítva) — kimenőnél null */
  html: string | null;
  text: string;
  sentAt: string;
  /** a választ író kolléga */
  author: string | null;
  status: 'ok' | 'failed';
  error: string | null;
  attachments: SupportMailAttachment[];
}

export interface SupportMailThreadDetail extends SupportMailThreadSummary {
  messages: SupportMailMessage[];
  orders: SupportMailOrderRef[];
  tickets: SupportMailTicketRef[];
  /** a vevő e-mail címe alapján talált, még nem kapcsolt rendelések */
  suggestedOrders: SupportMailOrderRef[];
  /** tiltott feladó: az új levelei egyenesen a Spam mappába mennek */
  senderBlocked: boolean;
  partner: SupportMailPartner | null;
}

export interface SupportMailboxSettings {
  enabled: boolean;
  email: string;
  from_name: string;
  username: string;
  has_password: boolean;
  imap_host: string;
  imap_port: number | null;
  imap_secure: boolean;
  smtp_host: string;
  smtp_port: number | null;
  smtp_secure: boolean;
  signature: string;
  configured: boolean;
  last_sync_at: string | null;
  last_sync_error: string | null;
}

export type UpdateSupportMailboxRequest = Partial<Omit<SupportMailboxSettings, 'has_password' | 'configured' | 'last_sync_at' | 'last_sync_error' | 'imap_port' | 'smtp_port'>> & {
  password?: string;
  imap_port?: number | string | null;
  smtp_port?: number | string | null;
};

@Injectable({ providedIn: 'root' })
export class SupportMailService {
  /** olvasatlan beszélgetések száma — a bal menü jelvénye ezt figyeli */
  readonly unread$ = new BehaviorSubject<number>(0);
  /** a postafiók végpontjai: support (Levelezés) vagy beszerzés (lásd PurchasingMailService) */
  protected readonly base: string = 'support/mail';

  constructor(protected http: HttpClient) {}

  getSettings(): Observable<SupportMailboxSettings> {
    return this.http.get<SupportMailboxSettings>(`${this.base}/settings`);
  }

  updateSettings(request: UpdateSupportMailboxRequest): Observable<SupportMailboxSettings> {
    return this.http.patch<SupportMailboxSettings>(`${this.base}/settings`, request);
  }

  /** IMAP + SMTP kapcsolat-teszt; null = rendben, szöveg = hibaüzenet */
  testSettings(): Observable<{ imap: string | null; smtp: string | null }> {
    return this.http.post<{ imap: string | null; smtp: string | null }>(`${this.base}/settings/test`, {});
  }

  /** Azonnali IMAP-szinkron */
  sync(): Observable<{ imported: number; skipped: number }> {
    return this.http.post<{ imported: number; skipped: number }>(`${this.base}/sync`, {});
  }

  refreshUnread(): void {
    this.http.get<{ unread: number }>(`${this.base}/unread-count`)
      .pipe(catchError(() => of({ unread: this.unread$.value })))
      .subscribe((result) => this.unread$.next(result?.unread || 0));
  }

  listThreads(filter: SupportMailFilter, search: string, page = 0, pageSize = 40): Observable<{ items: SupportMailThreadSummary[]; total: number }> {
    let params = new HttpParams().set('status', filter).set('page', `${page}`).set('page_size', `${pageSize}`);
    if (search.trim()) {
      params = params.set('search', search.trim());
    }
    return this.http.get<{ items: SupportMailThreadSummary[]; total: number }>(`${this.base}/threads`, { params });
  }

  /** `markRead`: megnyitáskor olvasottra vált */
  getThread(id: number, markRead = true): Observable<SupportMailThreadDetail> {
    return this.http.get<SupportMailThreadDetail>(`${this.base}/threads/${id}${markRead ? '?read=1' : ''}`)
      .pipe(tap(() => markRead && this.refreshUnread()));
  }

  updateThread(id: number, request: { status?: SupportMailThreadStatus; unread?: boolean }): Observable<SupportMailThreadDetail> {
    return this.http.patch<SupportMailThreadDetail>(`${this.base}/threads/${id}`, request).pipe(tap(() => this.refreshUnread()));
  }

  reply(id: number, request: { text: string; cc?: string; close?: boolean }, files: File[]): Observable<SupportMailThreadDetail> {
    const fd = new FormData();
    fd.append('text', request.text);
    if (request.cc) fd.append('cc', request.cc);
    if (request.close) fd.append('close', 'true');
    files.forEach((file) => fd.append('files', file, file.name));
    return this.http.post<SupportMailThreadDetail>(`${this.base}/threads/${id}/reply`, fd);
  }

  compose(request: { to: string; cc?: string; subject: string; text: string; orderId?: number | null; partnerName?: string }, files: File[]): Observable<SupportMailThreadDetail> {
    const fd = new FormData();
    fd.append('to', request.to);
    if (request.cc) fd.append('cc', request.cc);
    fd.append('subject', request.subject);
    fd.append('text', request.text);
    if (request.orderId) fd.append('orderId', `${request.orderId}`);
    if (request.partnerName) fd.append('partnerName', request.partnerName);
    files.forEach((file) => fd.append('files', file, file.name));
    return this.http.post<SupportMailThreadDetail>(`${this.base}/threads`, fd);
  }

  /** Feladó tiltása (a beszélgetés is a Spam mappába kerül) / feloldása */
  blockSender(threadId: number, blocked: boolean): Observable<SupportMailThreadDetail> {
    return this.http.post<SupportMailThreadDetail>(`${this.base}/threads/${threadId}/block-sender`, { blocked }).pipe(tap(() => this.refreshUnread()));
  }

  listPartners(): Observable<SupportMailPartner[]> {
    return this.http.get<SupportMailPartner[]>(`${this.base}/partners`);
  }

  savePartner(id: number | null, request: { name?: string; email?: string; note?: string }): Observable<SupportMailPartner> {
    return id
      ? this.http.patch<SupportMailPartner>(`${this.base}/partners/${id}`, request)
      : this.http.post<SupportMailPartner>(`${this.base}/partners`, request);
  }

  deletePartner(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/partners/${id}`);
  }

  addOrder(threadId: number, orderId: number): Observable<SupportMailThreadDetail> {
    return this.http.post<SupportMailThreadDetail>(`${this.base}/threads/${threadId}/orders`, { orderId });
  }

  removeOrder(threadId: number, orderId: number): Observable<SupportMailThreadDetail> {
    return this.http.delete<SupportMailThreadDetail>(`${this.base}/threads/${threadId}/orders/${orderId}`);
  }

  addTicket(threadId: number, ticketId: number): Observable<SupportMailThreadDetail> {
    return this.http.post<SupportMailThreadDetail>(`${this.base}/threads/${threadId}/tickets`, { ticketId });
  }

  removeTicket(threadId: number, ticketId: number): Observable<SupportMailThreadDetail> {
    return this.http.delete<SupportMailThreadDetail>(`${this.base}/threads/${threadId}/tickets/${ticketId}`);
  }

  /** Task-kereső: T-azonosító vagy cím */
  searchTickets(query: string): Observable<SupportMailTicketRef[]> {
    return this.http.get<SupportMailTicketRef[]>(`${this.base}/ticket-search`, { params: new HttpParams().set('q', query) });
  }

  downloadAttachment(id: number): Observable<Blob> {
    return this.http.get(`${this.base}/attachments/${id}`, { responseType: 'blob' });
  }
}

/** A beszerzési postafiók (beszerzes@) — ugyanaz a felület, más végpontok és külön olvasatlan-számláló. */
@Injectable({ providedIn: 'root' })
export class PurchasingMailService extends SupportMailService {
  protected override readonly base: string = 'purchasing/mail';

  constructor(http: HttpClient) {
    super(http);
  }
}
